import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAssessment } from '../context/AssessmentContext';

const MAX_RECORDING_SECONDS = 60;

const formatSeconds = (totalSeconds) => {
	const safeSeconds = Math.max(0, totalSeconds);
	const minutes = String(Math.floor(safeSeconds / 60)).padStart(2, '0');
	const seconds = String(safeSeconds % 60).padStart(2, '0');
	return `${minutes}:${seconds}`;
};

const SelfIntroPage = () => {
	const history = useHistory();
	const { assessmentId, uploadSelfIntro, selfIntroSubmitted } = useAssessment();

	const liveVideoRef = useRef(null);
	const mediaRecorderRef = useRef(null);
	const streamRef = useRef(null);
	const chunksRef = useRef([]);
	const isMountedRef = useRef(true);

	const [stream, setStream] = useState(null);
	const [isRecording, setIsRecording] = useState(false);
	const [timeLeft, setTimeLeft] = useState(MAX_RECORDING_SECONDS);
	const [recordedBlob, setRecordedBlob] = useState(null);
	const [previewUrl, setPreviewUrl] = useState('');
	const [error, setError] = useState(null);
	const [uploading, setUploading] = useState(false);

	useEffect(() => {
		if (!assessmentId) {
			history.replace('/instructions');
		}
	}, [assessmentId, history]);

	useEffect(() => {
		if (selfIntroSubmitted) {
			history.replace('/exam');
		}
	}, [history, selfIntroSubmitted]);

	const cleanupStream = useCallback(() => {
		if (streamRef.current) {
			streamRef.current.getTracks().forEach((track) => track.stop());
			streamRef.current = null;
			setStream(null);
		}
	}, []);

	const resetRecordingState = useCallback(() => {
		setIsRecording(false);
		setTimeLeft(MAX_RECORDING_SECONDS);
		setRecordedBlob(null);
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl('');
		}
		chunksRef.current = [];
	}, [previewUrl]);

	useEffect(() => {
		isMountedRef.current = true;
		return () => {
			isMountedRef.current = false;
			if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
				mediaRecorderRef.current.stop();
			}
			cleanupStream();
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
			}
		};
	}, [cleanupStream, previewUrl]);

	useEffect(() => {
		const video = liveVideoRef.current;
		if (!video) {
			return;
		}
		if (stream) {
			video.srcObject = stream;
			video.muted = true;
			video.playsInline = true;
			video.play().catch(() => undefined);
		} else {
			video.pause();
			video.srcObject = null;
		}
	}, [stream]);

	const stopRecording = useCallback(() => {
		if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
			mediaRecorderRef.current.stop();
		}
		setIsRecording(false);
	}, []);

	useEffect(() => {
		if (!isRecording) {
			return undefined;
		}
		if (timeLeft <= 0) {
			stopRecording();
			return undefined;
		}
		const intervalId = setInterval(() => {
			setTimeLeft((current) => (current > 0 ? current - 1 : 0));
		}, 1000);
		return () => clearInterval(intervalId);
	}, [isRecording, stopRecording, timeLeft]);

	const prepareRecorder = useCallback(async () => {
		if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
			throw new Error('Media recording is not supported in this browser.');
		}

		const supportedMimeTypes = [
			'video/webm;codecs=vp9,opus',
			'video/webm;codecs=vp8,opus',
			'video/webm;codecs=opus',
			'video/webm',
		];

		const mediaStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
		streamRef.current = mediaStream;
		setStream(mediaStream);

		const mimeType = supportedMimeTypes.find((type) => MediaRecorder.isTypeSupported(type)) || '';
		const recorder = new MediaRecorder(mediaStream, mimeType ? { mimeType } : undefined);
		chunksRef.current = [];

		recorder.ondataavailable = (event) => {
			if (event.data && event.data.size > 0) {
				chunksRef.current.push(event.data);
			}
		};

		recorder.onstop = () => {
			cleanupStream();
			const recordedChunks = chunksRef.current;
			chunksRef.current = [];
			mediaRecorderRef.current = null;
			if (!isMountedRef.current) {
				return;
			}
			const blob = new Blob(recordedChunks, { type: recorder.mimeType || 'video/webm' });
			setRecordedBlob(blob);
			const url = URL.createObjectURL(blob);
			setPreviewUrl(url);
		};

		mediaRecorderRef.current = recorder;
		return recorder;
	}, [cleanupStream]);

	const handleStartRecording = useCallback(async () => {
		if (isRecording || uploading) {
			return;
		}
		setError(null);
		try {
			resetRecordingState();
			const recorder = await prepareRecorder();
			setIsRecording(true);
			setTimeLeft(MAX_RECORDING_SECONDS);
			recorder.start();
		} catch (err) {
			cleanupStream();
			const message =
				err?.message ||
				'Unable to access your camera or microphone. Please check permissions and try again.';
			setError(message);
		}
	}, [cleanupStream, isRecording, prepareRecorder, resetRecordingState, uploading]);

	const handleRetry = useCallback(() => {
		if (isRecording) {
			stopRecording();
		}
		cleanupStream();
		resetRecordingState();
		setUploading(false);
		setError(null);
	}, [cleanupStream, isRecording, resetRecordingState, stopRecording]);

	const handleSubmit = useCallback(async () => {
		if (!recordedBlob) {
			setError('Please record your self introduction before submitting.');
			return;
		}
		if (!assessmentId) {
			setError('Your assessment session is no longer active. Please reload the page.');
			return;
		}
		setUploading(true);
		setError(null);
		try {
			const fileName = `self-introduction-${Date.now()}.webm`;
			const file = new File([recordedBlob], fileName, {
				type: recordedBlob.type || 'video/webm',
			});
			await uploadSelfIntro(file);
			history.replace('/exam');
		} catch (err) {
			if (isMountedRef.current) {
				const message =
					err?.response?.data?.detail?.message ||
					err?.message ||
					'Failed to upload your self introduction. Please try again.';
				setError(message);
			}
		} finally {
			if (isMountedRef.current) {
				setUploading(false);
			}
		}
	}, [assessmentId, history, recordedBlob, uploadSelfIntro]);

	if (!assessmentId || selfIntroSubmitted) {
		return null;
	}

	const secondsUsed = recordedBlob ? Math.max(0, MAX_RECORDING_SECONDS - timeLeft) : 0;
	const secondsToDisplay = isRecording ? timeLeft : recordedBlob ? secondsUsed : MAX_RECORDING_SECONDS;
	const timerClassName = `selfintro-timer${isRecording && secondsToDisplay <= 10 ? ' warning' : ''}`;
	const timerLabel = formatSeconds(secondsToDisplay);
	const statusMessage = isRecording
		? 'Recording in progress...'
		: recordedBlob
				? 'Review your recording before submitting.'
				: 'Press "Start Recording" to begin.';

	return (
		<div className="selfintro-page">
			<div className="selfintro-card">
				<h1>Self Introduction</h1>
				<p>
					Record a brief self introduction (maximum 1 minute). You can retry the recording before
					submitting.
				</p>
				<div className="selfintro-recorder">
					<div className="selfintro-video">
						{previewUrl ? (
							<video key={previewUrl} src={previewUrl} controls playsInline />
						) : (
							<video ref={liveVideoRef} autoPlay playsInline muted />
						)}
					</div>
					<div className="selfintro-status">
						<span className={timerClassName}>{timerLabel}</span>
						<p>{statusMessage}</p>
					</div>
				</div>
				{error && <div className="selfintro-error">{error}</div>}
				<div className="selfintro-actions">
					<button
						type="button"
						className="selfintro-primary"
						onClick={handleStartRecording}
						disabled={isRecording || uploading}
					>
						{isRecording ? 'Recording...' : 'Start Recording'}
					</button>
					<button
						type="button"
						className="selfintro-secondary"
						onClick={stopRecording}
						disabled={!isRecording}
					>
						Stop Recording
					</button>
					<button
						type="button"
						className="selfintro-secondary"
						onClick={handleRetry}
						disabled={isRecording || (!recordedBlob && !previewUrl)}
					>
						Retry
					</button>
					<button
						type="button"
						className="selfintro-primary"
						onClick={handleSubmit}
						disabled={!recordedBlob || uploading}
					>
						{uploading ? 'Uploading...' : 'Submit & Begin Assessment'}
					</button>
				</div>
			</div>
		</div>
	);
};

export default SelfIntroPage;